/**
 * Hook for platform admin authentication and authorization
 */

import { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useUser } from '@clerk/clerk-react';
import { platformAdminService, type PlatformAdmin } from '@/services/platformAdminService';

// Check if we're in local development
const isLocalDevelopment = () => {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    import.meta.env.DEV
  );
};

export function usePlatformAdmin() {
  const { user } = useUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminDetails, setAdminDetails] = useState<PlatformAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      // In local development, bypass Clerk authentication and grant admin access
      if (isLocalDevelopment()) {
        if (import.meta.env.DEV) {
          logger.info('🔓 Local development detected - granting platform admin access');
        }
        setIsAdmin(true);
        setAdminDetails({
          id: 'local-dev-admin',
          clerk_user_id: 'local-dev-user',
          email: 'dev@local.test',
          name: 'Local Development Admin',
          is_active: true,
          permissions: {
            manage_categories: true,
            manage_products: true,
            manage_system: true,
            manage_users: true,
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as PlatformAdmin);
        setLoading(false);
        return;
      }

      // Production: require Clerk authentication
      if (!user?.id) {
        setIsAdmin(false);
        setAdminDetails(null);
        setLoading(false);
        return;
      }

      if (import.meta.env.DEV) {
        logger.info('Checking admin status for user:', user.id);
      }

      try {
        const isAdminUser = await platformAdminService.isPlatformAdmin(user.id);
        if (import.meta.env.DEV) {
          logger.info('Admin check result:', { userId: user.id, isAdmin: isAdminUser });
        }
        setIsAdmin(isAdminUser);

        if (isAdminUser) {
          const details = await platformAdminService.getAdminDetails(user.id);
          setAdminDetails(details);
        }
      } catch (error) {
        logger.error('Error checking platform admin status:', error);
        setIsAdmin(false);
        setAdminDetails(null);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user?.id]);

  const hasPermission = (permission: keyof PlatformAdmin['permissions']): boolean => {
    if (!adminDetails || !adminDetails.is_active) {
      return false;
    }
    return adminDetails.permissions[permission] === true;
  };

  return {
    isAdmin,
    adminDetails,
    loading,
    hasPermission,
    canManageCategories: hasPermission('manage_categories'),
    canManageProducts: hasPermission('manage_products'),
    canManageSystem: hasPermission('manage_system'),
  };
}
